import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { DocSlotCard } from "@/features/transit/components/doc-slot-card"
import { LockedBanner } from "@/features/transit/components/locked-banner"
import { SectionTitle } from "@/features/transit/components/section-title"
import { StageDocGrid } from "@/features/transit/components/stage-doc-grid"
import { UploadButton } from "@/features/transit/components/upload-button"
import { UploadMetadataDialog } from "@/features/transit/components/upload-metadata-dialog"
import { UploadedDocRow } from "@/features/transit/components/uploaded-doc-row"
import {
  useDeleteRedevance,
  useRegisterRedevance,
} from "@/features/transit/hooks/use-doc-mutations"
import { useTransitUpload } from "@/features/transit/hooks/use-transit-upload"
import { useUploadWithMetadata } from "@/features/transit/hooks/use-upload-with-metadata"
import { usePatchOT } from "@/features/transit/hooks/use-transit-mutations"
import { isOTLocked } from "@/features/transit/lib/ot-math"
import type { OT } from "@/features/transit/api/types"

interface FormulePanelProps {
  ot: OT
}

export function FormulePanel({ ot }: FormulePanelProps) {
  return <FormulePanelInner key={ot.saigicNumber ?? ""} ot={ot} />
}

function FormulePanelInner({ ot }: FormulePanelProps) {
  const { t } = useTranslation("transit")
  const patch = usePatchOT(ot.id)
  const [draft, setDraft] = useState(ot.saigicNumber ?? "")
  const locked = isOTLocked(ot)

  return (
    <div className="flex flex-col gap-4">
      {locked ? <LockedBanner ot={ot} /> : null}
      <Card className="flex flex-col gap-4 p-5">
        <SectionTitle
          eyebrow={t("formule.eyebrow")}
          title={t("formule.title")}
          description={t("formule.description")}
        />

        <div className="grid grid-cols-1 items-end gap-3 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Label className="cursor-help">
                  {t("formule.saigicLabel")}
                  <span className="text-red-500">*</span>
                </Label>
              </TooltipTrigger>
              <TooltipContent>{t("formule.saigicHelp")}</TooltipContent>
            </Tooltip>
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t("formule.saigicPlaceholder")}
              disabled={locked}
              readOnly={locked}
              className={locked ? "disabled:opacity-100" : undefined}
              onBlur={() => {
                if (!locked && draft !== (ot.saigicNumber ?? "")) {
                  patch.mutate({ saigicNumber: draft.trim() || null })
                }
              }}
            />
          </div>

          <DocSlotCard ot={ot} slotId="formule" />
        </div>
      </Card>

      <StageDocGrid
        ot={ot}
        stage="FORMULE"
        exclude={["formule", "formuleDefinitive"]}
      />

      <RedevancesCard ot={ot} />

      <FormuleDefinitiveCard ot={ot} />
    </div>
  )
}

// "Formule définitive" closes out the FORMULE stage — number + doc.
// Both are required to advance to EMPOTAGE (backend blocker + the doc
// slot is registered as SlotRequiredAlways).
function FormuleDefinitiveCard({ ot }: { ot: OT }) {
  const { t } = useTranslation("transit")
  const patch = usePatchOT(ot.id)
  const locked = isOTLocked(ot)
  const [draft, setDraft] = useState(ot.formuleDefinitiveNumber ?? "")

  return (
    <Card className="flex flex-col gap-4 p-5">
      <SectionTitle
        eyebrow={t("formule.definitiveEyebrow")}
        title={t("formule.definitiveTitle")}
        description={t("formule.definitiveDescription")}
      />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label>
            {t("formule.definitiveNumberLabel")}
            <span className="text-red-500"> *</span>
          </Label>
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("formule.definitiveNumberPlaceholder")}
            disabled={locked}
            readOnly={locked}
            className={locked ? "disabled:opacity-100" : undefined}
            onBlur={() => {
              const next = draft.trim()
              if (!locked && next !== (ot.formuleDefinitiveNumber ?? "")) {
                patch.mutate({ formuleDefinitiveNumber: next || null })
              }
            }}
          />
        </div>
      </div>

      <DocSlotCard
        ot={ot}
        slotId="formuleDefinitive"
        hint={t("formule.definitiveDocHint")}
      />
    </Card>
  )
}

function RedevancesCard({ ot }: { ot: OT }) {
  const { t } = useTranslation("transit")
  const upload = useTransitUpload()
  const register = useRegisterRedevance(ot.id)
  const remove = useDeleteRedevance(ot.id)
  const rows = ot.redevances ?? []
  const locked = isOTLocked(ot)

  const meta = useUploadWithMetadata({
    kindLabel: t("formule.redevancesTitle"),
    upload: async (file, m) => {
      await upload.upload({
        otId: ot.id,
        kind: "redevance",
        file,
        register: async ({ storedKey, file: f }) =>
          register.mutateAsync({
            name: m.name,
            storedKey,
            docType: f.type || undefined,
            uploadedBy: m.uploadedBy || undefined,
            note: m.description || undefined,
          }),
      })
    },
  })

  return (
    <Card className="flex flex-col gap-3 p-5">
      <SectionTitle
        eyebrow={t("formule.redevancesEyebrow")}
        title={t("formule.redevancesTitle")}
        description={t("formule.redevancesDescription")}
        actions={
          locked ? null : (
            <UploadButton
              onPick={meta.onPick}
              isUploading={upload.isUploading || register.isPending}
              label={t("formule.redevancesAdd")}
            />
          )
        }
      />

      {rows.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed py-6 text-center text-sm">
          {t("formule.redevancesEmpty")}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((r) => (
            <li key={r.id}>
              <UploadedDocRow
                otId={ot.id}
                doc={r}
                trailingMeta={
                  <>
                    {r.amountFcfa
                      ? `${r.amountFcfa.toLocaleString()} FCFA`
                      : null}
                    {r.amountFcfa && r.reference ? " · " : null}
                    {r.reference}
                  </>
                }
                onDelete={() => remove.mutate(r.id)}
                isDeleting={remove.isPending}
                locked={locked}
              />
            </li>
          ))}
        </ul>
      )}
      <UploadMetadataDialog
        pending={meta.pending}
        onCancel={meta.onCancel}
        onConfirm={meta.onConfirm}
      />
    </Card>
  )
}
